import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

import budgetService from '../../services/budgetService';
import MonthSelector from './components/MonthSelector';
import BudgetCard from './components/BudgetCard';
import AddBudgetModal from './components/AddBudgetModal';
import EditBudgetModal from './components/EditBudgetModal';

import Icon from '../../components/AppIcon';
import UpgradeModal from '../../components/ui/UpgradeModal';
import Button from '../../components/ui/Button';
import Breadcrumbs from '../../components/ui/Breadcrumbs';

import Header from '../../components/ui/Header';


const BudgetManagement = () => {
  const { user } = useAuth();
  const { hasProAccess } = useSubscription();
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(new Date()?.getMonth());
  const [budgetProgress, setBudgetProgress] = useState([]);
  const [budgetLimits, setBudgetLimits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState({ feature: '', limit: 0, current: 0 });
  const [viewMode, setViewMode] = useState('tracker');
  const [incomeEstimate, setIncomeEstimate] = useState(0);
  const [budgetingMethod, setBudgetingMethod] = useState('traditional');

  const breadcrumbItems = [
    { label: 'Dashboard', path: '/dashboard' },
    { label: 'Budget Management', path: null }
  ];

  const currentYear = new Date()?.getFullYear();
  const monthYear = `${currentYear}-${String(selectedMonth + 1)?.padStart(2, '0')}`;

  useEffect(() => {
    if (user?.id) {
      loadBudgetData();
      loadBudgetingMethod();
    }
  }, [user?.id, monthYear]);

  const loadBudgetingMethod = async () => {
    try {
      const { data: profile, error } = await supabase?.from('user_profiles')?.select('budgeting_method')?.eq('id', user?.id)?.single();
      if (!error && profile?.budgeting_method) {
        setBudgetingMethod(profile?.budgeting_method);
      }
    } catch (err) {
      console.error('Error loading budgeting method:', err);
    }
  };

  const loadBudgetData = async () => {
    try {
      setLoading(true);
      setError('');
      const [progress, limits, income] = await Promise.all([
        budgetService?.getBudgetProgress(user?.id, monthYear),
        budgetService?.getBudgetLimits(user?.id, monthYear),
        budgetService?.getIncomeEstimate(user?.id, monthYear)
      ]);
      setBudgetProgress(progress);
      setBudgetLimits(limits);
      setIncomeEstimate(income || 0);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAddBudget = async (budgetData) => {
    try {
      await budgetService?.createBudgetLimit({
        ...budgetData,
        userId: user?.id,
        monthYear
      });
      await loadBudgetData();
      setShowAddModal(false);
    } catch (err) {
      throw err;
    }
  };

  const handleUpdateBudget = async (budgetId, updates) => {
    try {
      await budgetService?.updateBudgetLimit(budgetId, updates);
      await loadBudgetData();
      setEditingBudget(null);
    } catch (err) {
      throw err;
    }
  };

  const handleDeleteBudget = async (budgetId) => {
    if (!window.confirm('Are you sure you want to delete this budget limit?')) return;
    try {
      await budgetService?.deleteBudgetLimit(budgetId);
      await loadBudgetData();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleAddBudgetClick = async () => {
    setShowAddModal(true);
  };

  const getProgressColor = (percentageUsed, threshold1, threshold2) => {
    if (percentageUsed >= 100) return 'text-destructive';
    if (percentageUsed >= threshold2) return 'text-warning';
    if (percentageUsed >= threshold1) return 'text-warning';
    return 'text-success';
  };

  const getProgressBarColor = (percentageUsed, threshold1, threshold2) => {
    if (percentageUsed >= 100) return 'bg-destructive';
    if (percentageUsed >= threshold2) return 'bg-warning';
    if (percentageUsed >= threshold1) return 'bg-warning';
    return 'bg-success';
  };

  const mergedBudgetData = budgetLimits?.map(limit => {
    const progress = budgetProgress?.find(p => p?.budgetId === limit?.id);
    return {
      ...limit,
      currentSpending: progress?.currentSpending || 0,
      remainingBudget: progress?.remainingBudget || limit?.limitAmount,
      percentageUsed: progress?.percentageUsed || 0
    };
  });

  const totalBudgeted = mergedBudgetData?.reduce((sum, b) => sum + b?.limitAmount, 0);
  const totalSpent = mergedBudgetData?.reduce((sum, b) => sum + b?.currentSpending, 0);
  const totalRemaining = totalBudgeted - totalSpent;
  const overallPercentage = totalBudgeted > 0 ? (totalSpent / totalBudgeted * 100) : 0;

  // Calculate 50/30/20 allocations
  const needsAllocation = incomeEstimate * 0.5;
  const wantsAllocation = incomeEstimate * 0.3;
  const savingsAllocation = incomeEstimate * 0.2;

  // Categorize budgets for 50/30/20 method
  const needsCategories = ['Housing', 'Transportation', 'Groceries', 'Utilities', 'Healthcare', 'Insurance'];
  const wantsCategories = ['Entertainment', 'Dining Out', 'Shopping', 'Hobbies', 'Travel', 'Subscriptions'];
  
  const needsSpent = mergedBudgetData
    ?.filter(b => needsCategories?.some(cat => b?.category?.name?.includes(cat)))
    ?.reduce((sum, b) => sum + b?.currentSpending, 0);
  const wantsSpent = mergedBudgetData
    ?.filter(b => wantsCategories?.some(cat => b?.category?.name?.includes(cat)))
    ?.reduce((sum, b) => sum + b?.currentSpending, 0);
  const savingsSpent = incomeEstimate - totalSpent;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading budget data...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <Header />
      <div className="min-h-screen bg-background pt-20">
        <div className="container mx-auto px-16 md:px-24 lg:px-32 py-24 md:py-32 lg:py-40">
          <Breadcrumbs items={breadcrumbItems} />

          <div className="mb-24 md:mb-32">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-16 mb-16">
              <div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-8">
                  Budget Management
                </h1>
                <p className="text-base md:text-lg text-muted-foreground">
                  {viewMode === 'tracker' ? 'Track your spending against budget limits' : 'Plan your budget and compare with actual spending'}
                </p>
              </div>
              <Button
                variant="default"
                size="sm"
                iconName="Plus"
                iconPosition="left"
                onClick={() => setShowAddModal(true)}
                className="self-start sm:self-auto"
              >
                Add Budget
              </Button>
            </div>
          </div>

          <div className="mb-24 md:mb-32">
            <MonthSelector selectedMonth={selectedMonth} onMonthChange={setSelectedMonth} />
          </div>

          {/* View Mode Toggle */}
          <div className="mb-24 flex items-center gap-12 p-4 bg-muted rounded-lg w-fit">
            <button
              onClick={() => setViewMode('tracker')}
              className={`px-16 py-8 rounded-lg font-medium text-sm transition-smooth ${
                viewMode === 'tracker' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Budget Tracker
            </button>
            <button
              onClick={() => setViewMode('planner')}
              className={`px-16 py-8 rounded-lg font-medium text-sm transition-smooth ${
                viewMode === 'planner' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              Budget Planner
            </button>
          </div>

          {error && (
            <div className="mb-24 p-16 bg-destructive/10 border border-destructive rounded-lg">
              <p className="text-destructive">{error}</p>
            </div>
          )}

          {/* Budgeting Method Info Banner */}
          {budgetingMethod !== 'traditional' && (
            <div className="mb-24 p-20 bg-primary/10 border border-primary/20 rounded-lg">
              <div className="flex items-start gap-12">
                <Icon name="Info" size={20} color="var(--color-primary)" />
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4">
                    {budgetingMethod === 'zero_based' && 'Zero-Based Budgeting'}
                    {budgetingMethod === '50_30_20' && '50/30/20 Rule'}
                    {budgetingMethod === 'pay_yourself_first' && 'Pay Yourself First'}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {budgetingMethod === 'zero_based' && 'Every dollar is assigned a purpose. Income minus expenses should equal zero.'}
                    {budgetingMethod === '50_30_20' && '50% for needs, 30% for wants, 20% for savings and debt repayment.'}
                    {budgetingMethod === 'pay_yourself_first' && 'Prioritize savings first, then allocate remaining income to expenses.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {viewMode === 'planner' && (
            <>
              {/* Budget Planner Overview */}
              <div className="mb-32 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16">
                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-success/10 flex items-center justify-center">
                      <Icon name="TrendingUp" size={20} color="var(--color-success)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Income Estimate</p>
                      <p className="text-2xl font-bold text-foreground">
                        ${incomeEstimate?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Icon name="Target" size={20} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Total Budgeted</p>
                      <p className="text-2xl font-bold text-foreground">
                        ${totalBudgeted?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className="w-40 h-40 rounded-lg bg-warning/10 flex items-center justify-center">
                      <Icon name="DollarSign" size={20} color="var(--color-warning)" />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Actual Spending</p>
                      <p className="text-2xl font-bold text-foreground">
                        ${totalSpent?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-12">
                    <div className={`w-40 h-40 rounded-lg flex items-center justify-center ${
                      totalRemaining >= 0 ? 'bg-success/10' : 'bg-destructive/10'
                    }`}>
                      <Icon name="Wallet" size={20} color={totalRemaining >= 0 ? 'var(--color-success)' : 'var(--color-destructive)'} />
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Variance</p>
                      <p className={`text-2xl font-bold ${
                        totalRemaining >= 0 ? 'text-success' : 'text-destructive'
                      }`}>
                        {totalRemaining >= 0 ? '+' : ''}${totalRemaining?.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 50/30/20 Rule Specific Layout */}
              {budgetingMethod === '50_30_20' && (
                <div className="mb-32 grid grid-cols-1 md:grid-cols-3 gap-20">
                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-16">
                      <div className="w-40 h-40 rounded-lg bg-blue-500/10 flex items-center justify-center">
                        <Icon name="Home" size={20} color="#3b82f6" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Needs (50%)</p>
                        <p className="text-xl font-bold text-foreground">${needsAllocation?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Spent</span>
                        <span className="font-medium text-foreground">${needsSpent?.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-6">
                        <div
                          className={`h-6 rounded-full transition-all ${
                            (needsSpent / needsAllocation * 100) >= 100 ? 'bg-destructive' : 'bg-blue-500'
                          }`}
                          style={{ width: `${Math.min((needsSpent / needsAllocation * 100), 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Housing, utilities, groceries, transportation</p>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-16">
                      <div className="w-40 h-40 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Icon name="ShoppingBag" size={20} color="#a855f7" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Wants (30%)</p>
                        <p className="text-xl font-bold text-foreground">${wantsAllocation?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Spent</span>
                        <span className="font-medium text-foreground">${wantsSpent?.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-6">
                        <div
                          className={`h-6 rounded-full transition-all ${
                            (wantsSpent / wantsAllocation * 100) >= 100 ? 'bg-destructive' : 'bg-purple-500'
                          }`}
                          style={{ width: `${Math.min((wantsSpent / wantsAllocation * 100), 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Entertainment, dining, shopping, hobbies</p>
                    </div>
                  </div>

                  <div className="bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                    <div className="flex items-center gap-12 mb-16">
                      <div className="w-40 h-40 rounded-lg bg-success/10 flex items-center justify-center">
                        <Icon name="PiggyBank" size={20} color="var(--color-success)" />
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Savings (20%)</p>
                        <p className="text-xl font-bold text-foreground">${savingsAllocation?.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div className="flex justify-between text-xs">
                        <span className="text-muted-foreground">Saved</span>
                        <span className="font-medium text-foreground">${Math.max(0, savingsSpent)?.toFixed(2)}</span>
                      </div>
                      <div className="w-full bg-muted rounded-full h-6">
                        <div
                          className="h-6 rounded-full transition-all bg-success"
                          style={{ width: `${Math.min((Math.max(0, savingsSpent) / savingsAllocation * 100), 100)}%` }}
                        />
                      </div>
                      <p className="text-xs text-muted-foreground">Emergency fund, retirement, debt payoff</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Zero-Based Budgeting Specific Layout */}
              {budgetingMethod === 'zero_based' && (
                <div className="mb-32 bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center justify-between mb-16">
                    <h3 className="text-lg font-semibold text-foreground">Zero-Based Budget Status</h3>
                    <div className={`px-16 py-8 rounded-full text-sm font-medium ${
                      Math.abs(incomeEstimate - totalBudgeted) < 1 ? 'bg-success/20 text-success' : 'bg-warning/20 text-warning'
                    }`}>
                      {Math.abs(incomeEstimate - totalBudgeted) < 1 ? 'Balanced' : 'Needs Adjustment'}
                    </div>
                  </div>
                  <div className="space-y-12">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Income</span>
                      <span className="text-lg font-bold text-success">+${incomeEstimate?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Budgeted</span>
                      <span className="text-lg font-bold text-foreground">-${totalBudgeted?.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-border pt-12 flex justify-between items-center">
                      <span className="text-sm font-semibold text-foreground">Remaining to Allocate</span>
                      <span className={`text-xl font-bold ${
                        Math.abs(incomeEstimate - totalBudgeted) < 1 ? 'text-success' : 'text-warning'
                      }`}>
                        ${(incomeEstimate - totalBudgeted)?.toFixed(2)}
                      </span>
                    </div>
                  </div>
                  {Math.abs(incomeEstimate - totalBudgeted) >= 1 && (
                    <div className="mt-16 p-12 bg-warning/10 border border-warning/20 rounded-lg">
                      <p className="text-xs text-warning">
                        {incomeEstimate > totalBudgeted 
                          ? 'Allocate remaining income to reach zero-based budget' :'Reduce budgeted amounts to match income'}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Pay Yourself First Specific Layout */}
              {budgetingMethod === 'pay_yourself_first' && (
                <div className="mb-32 bg-card rounded-lg p-20 shadow-elevation-2 border border-border">
                  <div className="flex items-center gap-12 mb-16">
                    <Icon name="TrendingUp" size={24} color="var(--color-success)" />
                    <h3 className="text-lg font-semibold text-foreground">Savings Priority</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
                    <div className="p-16 bg-success/10 rounded-lg border border-success/20">
                      <p className="text-sm text-muted-foreground mb-8">Recommended Savings (20%)</p>
                      <p className="text-2xl font-bold text-success">${(incomeEstimate * 0.2)?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-8">Set aside first before expenses</p>
                    </div>
                    <div className="p-16 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground mb-8">Available for Expenses</p>
                      <p className="text-2xl font-bold text-foreground">${(incomeEstimate * 0.8)?.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground mt-8">After savings allocation</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Budget vs Actual Comparison */}
              {mergedBudgetData?.length === 0 ? (
                <div className="text-center py-40 bg-card rounded-lg border border-border">
                  <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                  <h3 className="text-xl font-semibold text-foreground mb-8">No Budget Limits Set</h3>
                  <p className="text-muted-foreground mb-24">Start by adding a budget limit for a category</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-24 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
                  >
                    Add Your First Budget
                  </button>
                </div>
              ) : (
                <div className="bg-card rounded-lg shadow-elevation-2 border border-border overflow-hidden">
                  <div className="p-20 border-b border-border">
                    <h2 className="text-lg font-semibold text-foreground">Budget vs Actual Comparison</h2>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-muted">
                        <tr>
                          <th className="px-16 py-12 text-left text-sm font-semibold text-foreground">Category</th>
                          <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Budgeted</th>
                          <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Actual</th>
                          <th className="px-16 py-12 text-right text-sm font-semibold text-foreground">Variance</th>
                          <th className="px-16 py-12 text-center text-sm font-semibold text-foreground">Status</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {mergedBudgetData?.map((budget) => {
                          const variance = budget?.limitAmount - budget?.currentSpending;
                          const variancePercentage = budget?.limitAmount > 0 ? (variance / budget?.limitAmount * 100) : 0;
                          
                          return (
                            <tr key={budget?.id} className="hover:bg-muted/50 transition-smooth">
                              <td className="px-16 py-16">
                                <div className="flex items-center gap-12">
                                  <div
                                    className="w-32 h-32 rounded-lg flex items-center justify-center"
                                    style={{ backgroundColor: budget?.category?.color || '#6366f1' }}
                                  >
                                    <Icon name={budget?.category?.icon || 'Tag'} size={16} color="white" />
                                  </div>
                                  <span className="text-sm font-medium text-foreground">
                                    {budget?.category?.name || 'Unknown'}
                                  </span>
                                </div>
                              </td>
                              <td className="px-16 py-16 text-right text-sm font-semibold text-foreground">
                                ${budget?.limitAmount?.toFixed(2)}
                              </td>
                              <td className="px-16 py-16 text-right text-sm font-semibold text-foreground">
                                ${budget?.currentSpending?.toFixed(2)}
                              </td>
                              <td className={`px-16 py-16 text-right text-sm font-bold ${
                                variance >= 0 ? 'text-success' : 'text-destructive'
                              }`}>
                                {variance >= 0 ? '+' : ''}${variance?.toFixed(2)}
                              </td>
                              <td className="px-16 py-16 text-center">
                                <div className="flex items-center justify-center gap-8">
                                  <div className="w-full max-w-[100px] bg-muted rounded-full h-6 overflow-hidden">
                                    <div
                                      className={`h-full transition-all duration-300 ${
                                        budget?.percentageUsed >= 100 ? 'bg-destructive' :
                                        budget?.percentageUsed >= 80 ? 'bg-warning' : 'bg-success'
                                      }`}
                                      style={{ width: `${Math.min(budget?.percentageUsed, 100)}%` }}
                                    ></div>
                                  </div>
                                  <span className="text-xs font-medium text-muted-foreground min-w-[40px]">
                                    {budget?.percentageUsed?.toFixed(0)}%
                                  </span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="bg-muted/50 border-t-2 border-border">
                        <tr>
                          <td className="px-16 py-16 text-sm font-bold text-foreground">Total</td>
                          <td className="px-16 py-16 text-right text-sm font-bold text-foreground">
                            ${totalBudgeted?.toFixed(2)}
                          </td>
                          <td className="px-16 py-16 text-right text-sm font-bold text-foreground">
                            ${totalSpent?.toFixed(2)}
                          </td>
                          <td className={`px-16 py-16 text-right text-sm font-bold ${
                            totalRemaining >= 0 ? 'text-success' : 'text-destructive'
                          }`}>
                            {totalRemaining >= 0 ? '+' : ''}${totalRemaining?.toFixed(2)}
                          </td>
                          <td className="px-16 py-16 text-center">
                            <span className={`text-xs font-bold px-12 py-4 rounded-full ${
                              overallPercentage >= 100 ? 'bg-destructive/20 text-destructive' :
                              overallPercentage >= 80 ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                            }`}>
                              {overallPercentage?.toFixed(0)}%
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {viewMode === 'tracker' && (
            <>
              {mergedBudgetData?.length === 0 ? (
                <div className="text-center py-40 bg-card rounded-lg border border-border">
                  <Icon name="TrendingUp" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-16" />
                  <h3 className="text-xl font-semibold text-foreground mb-8">No Budget Limits Set</h3>
                  <p className="text-muted-foreground mb-24">Start by adding a budget limit for a category</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="px-24 py-12 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth font-medium min-h-[44px]"
                  >
                    Add Your First Budget
                  </button>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between mb-16">
                    <h2 className="text-lg font-semibold text-foreground">Your Budgets</h2>
                    <span className="text-sm text-muted-foreground">{mergedBudgetData?.length} active budgets</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-20 md:gap-24">
                    {mergedBudgetData?.map((budget) => (
                      <BudgetCard
                        key={budget?.id}
                        budget={budget}
                        onEdit={() => setEditingBudget(budget)}
                        onDelete={() => handleDeleteBudget(budget?.id)}
                        getProgressColor={getProgressColor}
                        getProgressBarColor={getProgressBarColor}
                      />
                    ))}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {showAddModal && (
        <AddBudgetModal
          userId={user?.id}
          onClose={() => setShowAddModal(false)}
          onAdd={handleAddBudget}
        />
      )}
      {editingBudget && (
        <EditBudgetModal
          budget={editingBudget}
          onClose={() => setEditingBudget(null)}
          onUpdate={handleUpdateBudget}
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal
          isOpen={showUpgradeModal}
          onClose={() => setShowUpgradeModal(false)}
          feature={upgradeInfo?.feature}
          limit={upgradeInfo?.limit}
          current={upgradeInfo?.current}
        />
      )}
    </>
  );
};

export default BudgetManagement;